import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useNavigation } from '../../contexts/NavigationContext';
import type { BreadcrumbItem } from '../../types/navigation';

/**
 * Breadcrumb Navigation Component
 * Storyboard Legal - UX Enhancement Phase 1
 * 
 * Provides consistent breadcrumb navigation throughout the application
 * with support for dark theme, accessibility, and responsive design.
 */

interface BreadcrumbProps {
  /** Additional CSS classes */
  className?: string;
  /** Show home icon for first breadcrumb */
  showHomeIcon?: boolean;
}

/**
 * Breadcrumb component with Tailwind dark theme styling
 * Follows WCAG accessibility guidelines with proper ARIA labels
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({ 
  className = '',
  showHomeIcon = true 
}) => {
  const { state } = useNavigation();
  const { breadcrumbs, isLoading } = state;

  // Don't render if no breadcrumbs or only one item (current page)
  if (!breadcrumbs.length || breadcrumbs.length === 1) {
    return null;
  }

  return (
    <nav 
      className={`flex ${className}`} 
      aria-label="Breadcrumb navigation"
      role="navigation"
    >
      <ol className="flex items-center space-x-1 text-sm ml-4">
        {breadcrumbs.map((item, index) => (
          <BreadcrumbItem
            key={`${item.href || item.label}-${index}`}
            item={item}
            isFirst={index === 0}
            isLast={index === breadcrumbs.length - 1}
            showHomeIcon={showHomeIcon}
            isLoading={isLoading}
          />
        ))}
      </ol>
    </nav>
  );
};

interface BreadcrumbItemProps {
  item: BreadcrumbItem;
  isFirst: boolean;
  isLast: boolean;
  showHomeIcon: boolean;
  isLoading: boolean;
}

/**
 * Individual breadcrumb item component
 */
const BreadcrumbItem: React.FC<BreadcrumbItemProps> = ({
  item,
  isFirst,
  isLast,
  showHomeIcon,
  isLoading
}) => {
  const { label, href, current, icon: ItemIcon } = item;

  return (
    <li className="flex items-center">
      {/* Separator for non-first items */}
      {!isFirst && (
        <ChevronRight 
          className="flex-shrink-0 w-4 h-4 text-gray-400 mx-2" 
          aria-hidden="true"
        />
      )}
      
      {/* Breadcrumb content */}
      <div className="flex items-center">
        {/* Icon */}
        {(isFirst && showHomeIcon) ? (
          <Home className="flex-shrink-0 w-4 h-4 mr-2 text-gray-400" aria-hidden="true" />
        ) : ItemIcon ? (
          <ItemIcon className="flex-shrink-0 w-4 h-4 mr-2 text-gray-400" aria-hidden="true" />
        ) : null}

        {/* Loading state */}
        {isLoading && isLast ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-3 w-3 border-b-1 border-gray-400 mr-2"></div>
            <span className="text-gray-400">Loading...</span>
          </div>
        ) : current || !href ? (
          /* Current page - not clickable */
          <span
            className={`font-medium ${
              current 
                ? 'text-white' 
                : 'text-gray-300'
            }`}
            aria-current={current ? 'page' : undefined}
          >
            {label}
          </span>
        ) : (
          /* Clickable breadcrumb link */
          <Link
            to={href}
            className="text-gray-400 hover:text-gray-200 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-sm px-1 py-0.5"
            aria-label={`Navigate to ${label}`}
          >
            {label}
          </Link>
        )}
      </div>
    </li>
  );
};

/**
 * Utility component for pages that need simple breadcrumb setup
 */
export const SimpleBreadcrumb: React.FC<{
  items: Omit<BreadcrumbItem, 'current'>[];
  className?: string;
}> = ({ items, className }) => {
  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb navigation">
      <ol className="flex items-center space-x-1 text-sm">
        {items.map((item, index) => (
          <li key={`${item.href || item.label}-${index}`} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="flex-shrink-0 w-4 h-4 text-gray-400 mx-2" aria-hidden="true" />
            )}
            
            {index === 0 && (
              <Home className="flex-shrink-0 w-4 h-4 mr-2 text-gray-400" aria-hidden="true" />
            )}
            
            {item.href ? (
              <Link
                to={item.href}
                className="text-gray-400 hover:text-gray-200 transition-colors duration-200 font-medium"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-white font-medium">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
